import { useCallback, useMemo, useState, memo } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDialog } from '../../hooks/useDialog'
import {
  isSameItemExact,
  allModifiersSelected,
  flattenModifiers
} from './utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

export const MenuItem = memo(({
  item,
  order,
  setOrder,
  readOnly,
  reducedTax,
  excludeTax
}) => {
  const { isOpen, openDialog, closeDialog } = useDialog();
  const orderItems = useMemo(() =>
    order.filter(i => i._id.toString() === item._id.toString()),
    [item, order]
  );

  const handleIncrease = useCallback((modifiers) => {
    setOrder(prev => {
      const existingItem = prev.find(i =>
        isSameItemExact(i, item, modifiers)
      );

      if (existingItem) {
        const modifierTotal = modifiers.reduce((sum, m) => sum + m.price, 0) || 0;
        const singleItemTotal = item.price + modifierTotal;

        return prev.map(i =>
          isSameItemExact(i, item, modifiers)
            ? {
                ...i,
                quantity: i.quantity + 1,
                total: (i.quantity + 1) * singleItemTotal,
              }
            : i
        );
      }
      else {
        return [
          ...prev,
          {
            ...item,
            modifiers: modifiers || [],
            quantity: 1,
            total: item.price + (modifiers.reduce((sum, m) => sum + m.price, 0) || 0),
            reducedTax,
            excludeTax,
          }
        ];
      }
    });
  }, [item, setOrder, reducedTax, excludeTax]);

  const handleDecrease = useCallback((modifiers) => {
    setOrder(prev =>
      prev.reduce((acc, i) => {
        if (isSameItemExact(i, item, modifiers)) {
          const modifierTotal = modifiers.reduce((sum, m) => sum + m.price, 0);
          const unitPrice = item.price + modifierTotal;

          if (i.quantity > 1) {
            acc.push({
              ...i,
              quantity: i.quantity - 1,
              total: (i.quantity - 1) * unitPrice,
            });
          }
          // else skip adding to remove completely when quantity hits 0
        } else {
          acc.push(i);
        }
        return acc;
      }, [])
    );
  }, [item, setOrder]);

  return (
    <MenuItemRow
      item={item}
      orderItems={orderItems}
      readOnly={readOnly}
      onIncrease={handleIncrease}
      onDecrease={handleDecrease}
      isOpen={isOpen}
      openDialog={openDialog}
      closeDialog={closeDialog}
    />
  );
});

const MenuItemRow = memo(({
  item, orderItems, readOnly, onIncrease, onDecrease, isOpen, openDialog, closeDialog
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = useCallback(() => setExpanded(prev => !prev), []);

  const handleAddClick = useCallback(() => {
    if (item.modifiers?.length > 0) {
      openDialog('addOn');
    } else {
      onIncrease([]);
    }
  }, [item, openDialog, onIncrease]);

  const renderModifiers = useCallback(() =>
    orderItems.flatMap((i, index) =>
      <Box
        key={index}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          my: 1
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {i.modifiers.map((modifier) => modifier.name).join(', ')}
        </Typography>
        {orderItems.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#f6f3eb',
              borderRadius: 2,
              px: 1
            }}
          >
            <IconButton size="small" onClick={() => onDecrease(i.modifiers)}>
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography variant="body1" color="text.secondary" sx={{ mx: 1 }}>
              {i.quantity}
            </Typography>
          </Box>
        )}
      </Box>
  ), [onDecrease, orderItems]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flex: 1 }}>
          {/* Header: name and expand icon on same line */}
          <Box display="flex" alignItems="center" sx={{ cursor: 'pointer' }} onClick={handleToggle}>
            <Typography variant="subtitle1">{item.name}</Typography>
            {item.description && (
              <IconButton
                size="small"
                sx={{
                  ml: 1,
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s',
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            )}
          </Box>

          {/* Collapsible description directly below */}
          {item.description && (
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box onClick={handleToggle} sx={{ cursor: 'pointer' }}>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  {item.description}
                </Typography>
                </Box>
            </Collapse>
          )}

          {orderItems.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              ${item.price.toFixed(2)}
            </Typography>
          ) : (
            renderModifiers()
          )}
        </Box>

        {/* Quantity Control */}
        {orderItems.length !== 1 ? (
          <IconButton onClick={handleAddClick}
          disabled={readOnly}
            sx={{
              backgroundColor: '#4B7359',
              color: 'white',
              '&:hover': {
                color: '#4B7359',
                backgroundColor: '#f6f3eb',
              },
            }}
          >
            <AddIcon />
          </IconButton>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            sx={{ backgroundColor: '#f6f3eb', borderRadius: 2, px: 1 }}
          >
            <IconButton size="small" onClick={() => onDecrease([])}>
              <RemoveIcon />
            </IconButton>
            <Typography variant="body1">{orderItems[0].quantity}</Typography>
            <IconButton size="small" onClick={handleAddClick}>
              <AddIcon />
            </IconButton>
          </Stack>
        )}
      </Stack>
      {isOpen('addOn') && item.modifiers.length > 0 && (
        <MenuAddOnDialog
          modifiers={item.modifiers}
          onIncrease={onIncrease}
          isOpen={isOpen}
          closeDialog={closeDialog}
        />
      )}
    </Box>
  );
});

function MenuAddOnDialog({ modifiers, onIncrease, isOpen, closeDialog }) {
  const { t } = useTranslation();
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [error, setError] = useState(false);

  const handleModifierChange = useCallback((id, value) => {
    setSelectedModifiers(prev => ({
      ...prev,
      [id]: value
    }));
    setError(false);
  }, []);

  const handleSubmit = useCallback((event) => {
    event.preventDefault();
    if (!allModifiersSelected(modifiers, selectedModifiers)) {
      setError(true);
      return;
    }
    onIncrease(flattenModifiers(selectedModifiers));
    closeDialog('addOn');
  }, [onIncrease, closeDialog, modifiers, selectedModifiers]);

  return (
    <Dialog
      open={isOpen('addOn')}
      fullWidth
      onClose={() => closeDialog('addOn')}
      slotProps={{
          paper: {
              sx: {
                  position: 'relative',
                  maxHeight: '75vh',
              }
          },
      }}> 
      <DialogTitle>{t('addOnTitle')}</DialogTitle>
      <DialogContent>
        {error && (<Alert severity='error' sx={{mb: 2}}>{t('addOnError')}</Alert>)}
        <Stack spacing={0} divider={<Divider sx={{ my: 2 }} />}>
          {modifiers.map((modifier, i) => {
            if (modifier.selectOne) {
              return (<SelectOneModifier key={i} modifier={modifier} selected={selectedModifiers} onChange={handleModifierChange}/>);
            }
            else if (modifier.selectMulti) {
              return (<SelectManyModifier key={i} modifier={modifier} selected={selectedModifiers} onChange={handleModifierChange}/>);
            }
            else {
              return (<></>)
            }
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleSubmit}>
          {t('addOnButtonLabel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SelectOneModifier({ modifier, selected, onChange }) {
  const { t } = useTranslation();

  return (
      <FormControl>
        <FormLabel component="legend">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>{modifier.name}</span>
            <Chip label={`${t('addOnRequired')}`} size="small" />
          </Box>
        </FormLabel>
        <RadioGroup
          value={selected[modifier._id]?.name || ''}
          onChange={e => {
            const item = modifier.items.find(i => i.name === e.target.value);
            if (item) onChange(modifier._id, { name: item.name, price: item.price });
          }}
        >
          {modifier.items.map((item, i) => (
            <FormControlLabel
              key={i}
              value={item.name}
              control={<Radio />}
              label={`${item.name} ${item.price > 0 ? `($${item.price.toFixed(2)})` : ''}`}
            />
          ))}
        </RadioGroup>
      </FormControl>
  );
}

function SelectManyModifier({ modifier, selected, onChange }) {
  const selectedItems = selected[modifier._id] || [];

  const handleChange = (e, item) => {
    const checked = e.target.checked;
    let updated = [...selectedItems];

    const exists = updated.find(i => i.name === item.name);

    if (checked && !exists) {
      if (modifier.maxSelect === 0 || updated.length < modifier.maxSelect) {
        updated.push({ name: item.name, price: item.price });
      }
    } else if (!checked && exists) {
      updated = updated.filter(i => i.name !== item.name);
    }

    onChange(modifier._id, updated);
  };

  return (
    <FormControl component="fieldset">
      <FormLabel>{`${modifier.name} — ${modifier.maxSelect}`}</FormLabel>
      <FormGroup>
        {modifier.items.map((item, i) => (
          <FormControlLabel
            key={i}
            control={
              <Checkbox
                checked={
                  selectedItems.some(i => i.name === item.name)
                }
                onChange={(e) => handleChange(e, item)}
              />
            }
            label={`${item.name} ${item.price > 0 ? `($${item.price.toFixed(2)})` : ''}`}
          />
        ))}
      </FormGroup>
    </FormControl>
  );
};